import React from 'react';
import * as signalR from "@microsoft/signalr";
import IRider from '../../models/IRider';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import RidersTable from './RidersTable';
import { signalRTimingHubUrl } from '../../services/ServiceConstants';
import { IGroup } from '../../models/IEvent';
import i18n from '../../i18n/i18n';
import EventServiceInstance from '../../services/EventService';

const BLINK_DURATION_MS = 1000;
const REFRESH_INTERVAL_MS = 1 * 60 * 1000;

function getValues<T>(source: Record<string, T>): T[] {
    let result: T[] = [];
    for (var key in source) {
        result.push(source[key]);
    }
    return result;
}

function toRecord(source: IRider[]): Record<string, IRider> {
    let result: Record<string, IRider> = {};
    source.forEach(rider => {
        result[rider.tagIdFriendly] = rider;
    });
    return result;
}

interface IEventLiveState {
    riders: Record<string, IRider>;
    hubConnection: signalR.HubConnection | null;
    blinkingRiders: Set<string>;
}

interface IEventLiveProps extends WithSnackbarProps {
    eventId: string;
    isOrganizer?: boolean;
    groups?: IGroup[];
    unmasked?: boolean;
    riders?: IRider[];
    selectedRound?: string;
}

class LiveEvent extends React.Component<IEventLiveProps, IEventLiveState> {
    private blinkTimers: Record<string, ReturnType<typeof setTimeout>> = {};
    private refreshInterval: ReturnType<typeof setInterval> | null = null;
    private refreshInProgress: boolean = false;

    constructor(props: IEventLiveProps) {
        super(props);
        this.state = {
            riders: toRecord(props.riders ?? []),
            hubConnection: null,
            blinkingRiders: new Set()
        }
    }

    componentDidMount() {
        // Periodically refresh all riders every two minutes
        this.refreshInterval = setInterval(async () => {
            if (this.refreshInProgress) {
                return;
            }
            this.refreshInProgress = true;
            try {
                const riders = await EventServiceInstance.getRiders(this.props.eventId, this.props.unmasked);
                this.setState({ riders: toRecord(riders) });
            } catch (e) {
                console.error('Failed to refresh riders', e);
            } finally {
                this.refreshInProgress = false;
            }
        }, REFRESH_INTERVAL_MS);

        // Builds the SignalR connection, mapping it to /timing
        const hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(signalRTimingHubUrl)
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this.setState({ hubConnection: hubConnection }, () => {
            // Starts the SignalR connection
            this.state.hubConnection?.start().then(() => {
                // Once started, invokes the sendConnectionId in our TimingHub inside our ASP.NET Core application.
                if (this.state.hubConnection?.connectionId) {
                    this.state.hubConnection?.invoke("JoinRace", this.props.eventId);
                }
            }).catch(e => console.error(e));

            // Receive lap times
            this.state.hubConnection?.on("publishTime", (rider: IRider) => {
                
                // Use functional setState to avoid reading stale state when multiple events arrive simultaneously
                this.setState(prev => {
                    const riders = { ...prev.riders, [rider.tagIdFriendly]: rider };
                    const blinkingRiders = new Set(prev.blinkingRiders);
                    blinkingRiders.add(rider.tagIdFriendly);
                    return { riders, blinkingRiders };
                });

                // Clear any existing blink timer for this rider
                if (this.blinkTimers[rider.tagIdFriendly]) {
                    clearTimeout(this.blinkTimers[rider.tagIdFriendly]);
                }
                // Stop blinking after BLINK_DURATION_MS
                this.blinkTimers[rider.tagIdFriendly] = setTimeout(() => {
                    this.setState(prev => {
                        const updated = new Set(prev.blinkingRiders);
                        updated.delete(rider.tagIdFriendly);
                        return { blinkingRiders: updated };
                    });
                    delete this.blinkTimers[rider.tagIdFriendly];
                }, BLINK_DURATION_MS);


                // always show snack with best time, last time and lap count
                const lapMessage: string = i18n.t('common:rider.newLapNotification', {
                    name: rider.name,
                    bestTime: rider.bestTime,
                    lastTime: rider.lastTime,
                    lapCount: rider.lapCount
                });
                this.props.enqueueSnackbar(lapMessage);
            });

            // Receive rider updates (e.g. group or trial changes)
            this.state.hubConnection?.on("publishRiderUpdate", (rider: IRider) => {
                this.setState(prev => ({ riders: { ...prev.riders, [rider.tagIdFriendly]: rider } }));
                this.props.enqueueSnackbar(`${rider.name} - updated`);
            });

            // Receive rider deletions
            this.state.hubConnection?.on("deleteRider", (message: { raceId: string, tagId: string }) => {
                if (message.raceId !== this.props.eventId) {
                    return;
                }
                this.setState(prev => {
                    const riders = { ...prev.riders };
                    delete riders[message.tagId];
                    return { riders };
                });
            });
        });
    }

    componentDidUpdate(prevProps: IEventLiveProps) {
        if (prevProps.riders !== this.props.riders) {
            this.setState({ riders: toRecord(this.props.riders ?? []) });
        }
    }

    componentWillUnmount() {
        this.state.hubConnection?.stop().then(() => console.log("event connection closed"));
        // Clear all blink timers
        Object.values(this.blinkTimers).forEach(timer => clearTimeout(timer));
        this.blinkTimers = {};
        // Clear the periodic refresh
        if (this.refreshInterval !== null) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    render() {
        return (<RidersTable
            eventId={this.props.eventId}
            riders={getValues(this.state.riders)}
            isOrganizer={this.props.isOrganizer}
            groups={this.props.groups}
            blinkingRiders={this.state.blinkingRiders}
            selectedRound={this.props.selectedRound}
        />);
    }
}

export default withSnackbar(LiveEvent);